import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Sparkles, Upload, Loader2, Image as ImageIcon, RefreshCw } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const CreateArticle = () => {
  const navigate = useNavigate();
  
  // Article form state
  const [topic, setTopic] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [description, setDescription] = useState('');
  const [keywords, setKeywords] = useState('');
  const [category, setCategory] = useState('local-news');
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  
  // Generated article data
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [tags, setTags] = useState([]);
  
  // Image state
  const [selectedImage, setSelectedImage] = useState('');
  const [suggestedImages, setSuggestedImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Step control
  const [step, setStep] = useState(1); // 1: Input, 2: Generated Content, 3: Select Image

  const handleGenerateArticle = async () => {
    if (!topic.trim()) {
      alert('Please enter a topic for the article');
      return;
    }

    setGenerating(true);
    try {
      // Generate article content
      const response = await axios.post(`${API_BASE_URL}/api/admin/generate-article`, {
        topic: topic,
        url: sourceUrl,
        description: description,
        keywords: keywords,
        category: category,
        author: "Central Coast Times"
      });

      setTitle(response.data.title);
      setContent(response.data.content);
      setExcerpt(response.data.excerpt);
      setTags(response.data.suggested_tags || []);
      
      // Move to review step
      setStep(2);
      
    } catch (error) {
      alert('Failed to generate article: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGenerating(false);
    }
  };

  const handleSelectImage = () => {
    // If image already selected, allow direct publish
    if (selectedImage) {
      // User can still go to Step 3 to change image
      setStep(3);
    } else {
      // Move to image selection step and load AI images
      setStep(3);
      handleGetAIImages();
    }
  };

  const handleGetAIImages = async () => {
    setLoadingImages(true);
    try {
      // Use vision expert to get images based on article title/topic
      const searchQuery = title || topic;
      const response = await axios.post(`${API_BASE_URL}/api/admin/get-ai-images`, {
        query: searchQuery,
        category: category
      });

      if (response.data.images && response.data.images.length > 0) {
        setSuggestedImages(response.data.images);
      } else {
        alert('No images found. Please try uploading your own image.');
      }
    } catch (error) {
      alert('Failed to fetch AI images: ' + (error.response?.data?.detail || error.message));
      // Provide some fallback images
      setSuggestedImages([]);
    } finally {
      setLoadingImages(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_BASE_URL}/api/admin/upload-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Backend now returns full URL (https://cctimes.au/uploads/articles/...)
      const uploadedUrl = response.data.url;
      setSelectedImage(uploadedUrl);
      console.log('Image uploaded successfully:', uploadedUrl);
    } catch (error) {
      alert('Failed to upload image: ' + error.message);
      console.error('Upload error:', error);
    } finally {
      setUploadingImage(false);
    }
  };

  const handlePublish = async () => {
    if (!title || !content || !excerpt) {
      alert('Please generate article content first');
      return;
    }

    if (!selectedImage) {
      alert('Please select or upload an image');
      return;
    }

    setPublishing(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/admin/publish-article`, {
        title: title,
        content: content,
        excerpt: excerpt,
        category: category,
        author: "Central Coast Times",
        image: selectedImage,
        photo_urls: [selectedImage],
        tags: tags,
        featured: true
      });

      alert('Article published successfully! 🎉');
      navigate('/admin');
    } catch (error) {
      alert('Failed to publish article: ' + (error.response?.data?.detail || error.message));
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <Button 
        variant="outline" 
        onClick={() => navigate('/admin')}
        className="mb-4"
      >
        <ArrowLeft size={16} className="mr-2" />
        Back to Admin
      </Button>

      {/* Step 1: Generate Article */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="text-purple-600" />
              Create New Article with AI
            </CardTitle>
            <CardDescription>
              Provide details and let AI generate a complete article for you
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <label className="block text-sm font-medium mb-2">Article Topic / Title *</label>
              <input
                type="text"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g., Local Beach Cleanup Success, New Restaurant Opens..."
                className="w-full px-4 py-3 border rounded-lg text-lg"
                disabled={generating}
              />
              <p className="text-xs text-gray-500 mt-1">
                Main topic or headline for the article
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Source URL (Optional)</label>
              <input
                type="url"
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
                placeholder="https://example.com/source-article"
                className="w-full px-4 py-3 border rounded-lg"
                disabled={generating}
              />
              <p className="text-xs text-gray-500 mt-1">
                Reference URL if this article is based on another source
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Article Description / Instructions</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what you want in the article. E.g., Focus on community impact, mention local businesses involved, highlight environmental benefits..."
                rows={4}
                className="w-full px-4 py-3 border rounded-lg"
                disabled={generating}
              />
              <p className="text-xs text-gray-500 mt-1">
                Tell AI what to focus on and what to include in the article
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Keywords</label>
              <input
                type="text"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                placeholder="e.g., community, environment, local business, sustainability"
                className="w-full px-4 py-3 border rounded-lg"
                disabled={generating}
              />
              <p className="text-xs text-gray-500 mt-1">
                Comma-separated keywords to include in the article
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Category *</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-4 py-3 border rounded-lg"
                disabled={generating}
              >
                <option value="local-news">Local News</option>
                <option value="community">Community</option>
                <option value="sports">Sports</option>
                <option value="youth-sports">Youth Sports</option>
                <option value="entertainment">Entertainment</option>
                <option value="education">Education</option>
                <option value="environment">Environment</option>
                <option value="business">Business</option>
                <option value="health">Health</option>
                <option value="infrastructure">Infrastructure</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Upload Article Image (Optional)</label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <input
                  type="file"
                  id="initial-image-upload"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  disabled={uploadingImage || generating}
                />
                <label
                  htmlFor="initial-image-upload"
                  className="cursor-pointer flex flex-col items-center gap-2"
                >
                  {uploadingImage ? (
                    <Loader2 size={32} className="text-gray-400 animate-spin" />
                  ) : (
                    <Upload size={32} className="text-gray-400" />
                  )}
                  <span className="text-sm font-medium">
                    {uploadingImage ? 'Uploading...' : selectedImage ? '✓ Image Uploaded' : 'Click to Upload Image'}
                  </span>
                  <span className="text-xs text-gray-500">
                    {selectedImage ? 'Click to change image' : 'JPG, PNG (you can also add this later)'}
                  </span>
                </label>
              </div>
              {selectedImage && (
                <div className="mt-3">
                  <img src={selectedImage} alt="Uploaded" className="w-full max-h-48 object-cover rounded-lg" />
                </div>
              )}
            </div>

            <Button 
              onClick={handleGenerateArticle}
              disabled={generating || !topic.trim()}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white py-6 text-lg"
            >
              {generating ? (
                <>
                  <Loader2 size={20} className="mr-2 animate-spin" />
                  Generating Article with AI...
                </>
              ) : (
                <>
                  <Sparkles size={20} className="mr-2" />
                  Generate Article
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Review Generated Article */}
      {step === 2 && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Review Generated Article</CardTitle>
              <CardDescription>
                Edit the content if needed, then select an image
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Excerpt</label>
                <textarea
                  value={excerpt}
                  onChange={(e) => setExcerpt(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Content (HTML)</label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={15}
                  className="w-full px-3 py-2 border rounded-lg font-mono text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Tags</label>
                <input
                  type="text"
                  value={tags.join(', ')}
                  onChange={(e) => setTags(e.target.value.split(',').map(t => t.trim()))}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Separate tags with commas"
                />
              </div>

              {selectedImage && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-green-800 mb-2">✓ Image Already Selected</p>
                  <img src={selectedImage} alt="Selected" className="w-full max-h-48 object-cover rounded" />
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  onClick={() => setStep(1)}
                  variant="outline"
                >
                  Back
                </Button>
                {selectedImage ? (
                  <>
                    <Button
                      onClick={handleSelectImage}
                      variant="outline"
                      className="flex-1"
                    >
                      Change Image
                    </Button>
                    <Button
                      onClick={handlePublish}
                      disabled={publishing}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      {publishing ? (
                        <>
                          <Loader2 size={18} className="mr-2 animate-spin" />
                          Publishing...
                        </>
                      ) : (
                        'Publish Now'
                      )}
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={handleSelectImage}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                  >
                    Next: Select Image
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Step 3: Select Image */}
      {step === 3 && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="text-blue-600" />
                Select Article Image
              </CardTitle>
              <CardDescription>
                Choose from AI-suggested images or upload your own
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Upload Custom Image */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <input
                  type="file"
                  id="image-upload"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  disabled={uploadingImage}
                />
                <label
                  htmlFor="image-upload"
                  className="cursor-pointer flex flex-col items-center gap-2"
                >
                  {uploadingImage ? (
                    <Loader2 size={32} className="text-gray-400 animate-spin" />
                  ) : (
                    <Upload size={32} className="text-gray-400" />
                  )}
                  <span className="text-sm font-medium">
                    {uploadingImage ? 'Uploading...' : 'Upload Your Own Image'}
                  </span>
                  <span className="text-xs text-gray-500">
                    Click to browse files (JPG, PNG)
                  </span>
                </label>
              </div>

              {/* AI Suggested Images */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium">AI-Suggested Images</h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleGetAIImages}
                    disabled={loadingImages}
                  >
                    {loadingImages ? (
                      <Loader2 size={14} className="mr-1 animate-spin" />
                    ) : (
                      <RefreshCw size={14} className="mr-1" />
                    )}
                    Refresh
                  </Button>
                </div>

                {loadingImages ? (
                  <div className="text-center py-12">
                    <Loader2 size={32} className="mx-auto animate-spin text-gray-400" />
                    <p className="text-sm text-gray-500 mt-2">Finding perfect images...</p>
                  </div>
                ) : suggestedImages.length > 0 ? (
                  <div className="grid grid-cols-3 gap-4">
                    {suggestedImages.map((imageUrl, index) => (
                      <div
                        key={index}
                        onClick={() => setSelectedImage(imageUrl)}
                        className={`cursor-pointer rounded-lg overflow-hidden border-4 transition-all ${
                          selectedImage === imageUrl
                            ? 'border-blue-600 shadow-lg'
                            : 'border-transparent hover:border-gray-300'
                        }`}
                      >
                        <img
                          src={imageUrl}
                          alt={`Option ${index + 1}`}
                          className="w-full h-48 object-cover"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>No AI images available yet.</p>
                    <p className="text-sm">Click refresh or upload your own image.</p>
                  </div>
                )}
              </div>

              {/* Selected Image Preview */}
              {selectedImage && (
                <div>
                  <h3 className="font-medium mb-2">Selected Image:</h3>
                  <img
                    src={selectedImage}
                    alt="Selected"
                    className="w-full max-h-64 object-cover rounded-lg"
                  />
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={() => setStep(2)}
                  variant="outline"
                >
                  Back
                </Button>
                <Button
                  onClick={handlePublish}
                  disabled={publishing || !selectedImage}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {publishing ? (
                    <>
                      <Loader2 size={18} className="mr-2 animate-spin" />
                      Publishing...
                    </>
                  ) : (
                    'Publish Article'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CreateArticle;
